import React, {
  CSSProperties,
  forwardRef,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Move, ArrowUp, ArrowDown } from "react-feather";
import {
  Droppable,
  Draggable,
  DragDropContext,
  DraggableProvided,
  DraggableStateSnapshot,
} from "@hello-pangea/dnd";
import { css, styled } from "styled-components";
import { NavLink } from "react-router-dom";
import Banner from "./Banner";
import Spinner from "./Spinner";
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  Grid,
} from "react-virtualized";
import RefResizeObserver from "rc-resize-observer";

export interface Row {
  id: string;
  element: (sorting: boolean) => ReactNode;
  link?: string;
  onClick?: () => void;
  active?: boolean;
  href?: string;
  target?: string;
  style?: CSSProperties;
}

interface AutoHeight {
  twoColumn?: boolean;
  minWidth?: string;
}

interface ColumnDef {
  title?: React.ReactNode;
  width: string;
  onSort?: (direction: "asc" | "desc") => void;
  defaultSort?: "asc" | "desc";
}

interface Props {
  columns?: ColumnDef[];
  isSorting?: boolean;
  sortingTitle?: string;
  isLoading?: boolean;
  isLoadingTop?: boolean;
  onSort?: (from: number, to: number) => void;
  rows?: Row[];
  rowHeight?: number;
  maxHeight?: string;
  height?: string;
  columnGrid?: boolean;
  rowPadding?: string;
  virtual?: boolean;
  autoHeight?: AutoHeight;
  virtualRowHeight?: number;
  onScroll?: (rowStartIndex: number, rowStopIndex: number) => void;
  className?: string;
  initialSort?: { columnIndex: number; direction: "asc" | "desc" };
}

export interface TableRef {
  resetScroll: () => void;
  scrollToRow: (row: number) => void;
}

const getScrollbarWidth = () => {
  const outer = document.createElement("div");
  outer.style.visibility = "hidden";
  outer.style.width = "100px";
  document.body.appendChild(outer);

  const widthNoScroll = outer.offsetWidth;
  outer.style.overflow = "scroll";

  const inner = document.createElement("div");
  inner.style.width = "100%";
  outer.appendChild(inner);

  const widthWithScroll = inner.offsetWidth;
  outer.parentNode?.removeChild(outer);

  return widthNoScroll - widthWithScroll;
};

const StyledTable = styled.div<{ $autoHeight?: AutoHeight }>`
  font-size: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;

  ${(props) =>
    props.$autoHeight &&
    css`
      max-height: ${props.$autoHeight?.twoColumn ? "35vh" : "75vh"};

      @media (min-width: ${props.$autoHeight?.minWidth ?? "1030px"}) {
        max-height: 75vh;
      }
    `}

  > header {
    color: var(--gray300);
    min-height: 30px;
    background-color: var(--dark300);
    position: relative;
    display: grid;
    column-gap: 10px;
    align-items: center;
    padding: 0 10px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
    flex: 0 0 auto;

    > div {
      display: flex;
      align-items: center;

      svg {
        margin-right: 5px;
      }
    }

    span {
      font-weight: 600;
    }
  }
`;

const Scroll = styled.div<{ $maxHeight?: string; $minWidth?: string }>`
  overflow-y: auto;
  flex: 1 1 auto;
  position: relative;

  /* Group consecutive selected rows into a single visual selection */
  .active:has(+ .active):not(.active + .active) {
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
    box-shadow: inset 0 1px 0 0 var(--blueBorder),
      inset 1px 0 0 0 var(--blueBorder), inset -1px 0 0 0 var(--blueBorder) !important;
  }
  .active + .active:has(+ .active) {
    border-radius: 0 !important;
    box-shadow: inset 1px 0 0 0 var(--blueBorder),
      inset -1px 0 0 0 var(--blueBorder) !important;
  }
  .active + .active:not(:has(+ .active)) {
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
    box-shadow: inset 0 -1px 0 0 var(--blueBorder),
      inset 1px 0 0 0 var(--blueBorder), inset -1px 0 0 0 var(--blueBorder) !important;
  }
`;

const TableRow = styled(NavLink)<{
  $dragging?: boolean;
  $sorting?: boolean;
  $link?: boolean;
  $columnGrid?: boolean;
  $odd?: boolean;
  $virtual?: boolean;
}>`
  white-space: pre-wrap;
  width: 100%;
  background-color: var(--dark100);
  position: relative;
  color: var(--gray300);

  ${(props) =>
    !props.$virtual &&
    css`
      display: flex;
    `}

  > * {
    flex-grow: 1;
  }

  padding: 5px 10px;

  ${(props) =>
    props.$odd &&
    css`
      background-color: var(--dark110);
    `}

  ${(props) =>
    props.$link &&
    css`
      cursor: pointer;

      &:hover {
        background-color: var(--dark200);
      }
    `}

  &.active {
    background-color: var(--blueBackground) !important;
    color: var(--gray500);
    box-shadow: 0 0 0 1px inset var(--blueBorder);
    border-radius: 10px;
  }

  ${(props) =>
    props.$dragging &&
    css`
      background-color: var(--dark200);
      box-shadow: none;
    `}

  ${(props) =>
    props.$columnGrid &&
    css`
      display: grid;
      column-gap: 10px;
      align-items: center;

      > * {
        overflow: hidden;
        text-overflow: ellipsis;
      }
    `}
`;

const ResizeWrapper = styled.div`
  display: grid;
  column-gap: 10px;
  align-items: center;

  > * {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Reorder = styled.div`
  color: var(--dark700);
  display: flex;
  align-items: center;

  > span {
    font-weight: 400 !important;
  }
`;

const SortableHeader = styled.button<{ $active: boolean }>`
  all: unset;
  display: flex;
  align-items: center;
  gap: 3px;
  cursor: pointer;
  user-select: none;
  color: ${(props) => (props.$active ? "var(--blue300)" : "var(--blue300)")};

  &:hover {
    color: var(--blue500);
    background: none;
  }

  > svg {
    flex-shrink: 0;
    opacity: ${(props) => (props.$active ? 1 : 0.4)};
  }
`;

const AutoSizeParent = styled.div`
  flex: 1 1 auto;
`;

const Table = forwardRef<TableRef, Props>((props, ref) => {
  const {
    columns,
    isSorting,
    onSort,
    rows,
    rowHeight,
    virtualRowHeight: virtualRowHeightProp,
    height,
    maxHeight,
    columnGrid,
    isLoading,
    isLoadingTop,
    sortingTitle,
    rowPadding,
    virtual,
    autoHeight,
    onScroll,
    className,
    initialSort,
  } = props;
  const [isOverflow, setIsOverflow] = useState(false);
  const [sortState, setSortState] = useState<{
    columnIndex: number;
    direction: "asc" | "desc";
  } | null>(initialSort ?? null);

  const handleColumnSort = (index: number, column: ColumnDef) => {
    if (!column.onSort) {
      return;
    }
    const direction =
      sortState?.columnIndex === index
        ? sortState.direction === "asc"
          ? "desc"
          : "asc"
        : column.defaultSort ?? "asc";
    setSortState({ columnIndex: index, direction });
    column.onSort(direction);
  };

  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollWidth = useMemo(getScrollbarWidth, []);
  const gridRef = useRef<Grid>(null);
  const [, setCurrentWidth] = useState(0);
  const [, setVisibleRows] = useState<{ first: number; last: number }>({
    first: 0,
    last: 0,
  });

  const isVirtual = virtual;
  const currentRowHeight = rowHeight ?? 50;
  const virtualRowHeight = virtualRowHeightProp ?? currentRowHeight;

  const cache = useMemo(
    () =>
      new CellMeasurerCache({
        defaultHeight: virtualRowHeight,
        fixedWidth: true,
      }),
    [virtualRowHeight]
  );

  useImperativeHandle(ref, () => ({
    resetScroll: () => {
      scrollRef.current?.scrollTo(0, 0);
      gridRef.current?.scrollToPosition({ scrollTop: 0, scrollLeft: 0 });
    },
    scrollToRow: (row: number) => {
      scrollRef.current?.scrollTo(0, row * currentRowHeight);
      gridRef.current?.scrollToPosition({
        scrollTop: row * currentRowHeight,
        scrollLeft: 0,
      });
    },
  }));

  const updateOverflow = () => {
    const element = scrollRef.current;
    if (element) {
      const hasOverflow = element.scrollHeight > element.clientHeight;
      if (hasOverflow && !isOverflow) {
        setIsOverflow(true);
      } else if (!hasOverflow && isOverflow) {
        setIsOverflow(false);
      }
    }
  };

  useEffect(() => {
    updateOverflow();
  }, [rows?.length]);

  const rowPaddingRight = (() => {
    if (!rowPadding) {
      return 10;
    }
    const parts = rowPadding.trim().split(/\s+/);
    return parseInt(parts[Math.min(1, parts.length - 1)], 10) || 0;
  })();

  const makeRow = (
    { element, id, link, onClick, active, href, target, style }: Row,
    index: number,
    options?: {
      style?: any;
      provided?: DraggableProvided;
      snapshot?: DraggableStateSnapshot;
      ref?: (element?: Element | null) => void;
      onResize?: (size: { width: number; height: number }) => void;
    }
  ) => (
    <TableRow
      as={link ? undefined : href ? "a" : "div"}
      to={link ?? ""}
      key={id}
      href={href}
      target={target}
      onClick={onClick}
      $link={!!(link || onClick || href)}
      ref={options?.ref ?? options?.provided?.innerRef}
      {...options?.provided?.draggableProps}
      {...options?.provided?.dragHandleProps}
      $dragging={options?.snapshot?.isDragging}
      $sorting={isSorting ?? false}
      title={isSorting ? "Drag to reorder" : undefined}
      className={active ? "active" : undefined}
      $columnGrid={options?.onResize ? false : columnGrid}
      $odd={index % 2 === 1}
      $virtual={virtual}
      style={{
        ...options?.provided?.draggableProps.style,
        ...options?.style,
        gridTemplateColumns: columnGrid
          ? columns?.map(({ width }) => width).join(" ")
          : undefined,
        minHeight: currentRowHeight,
        padding: rowPadding,
        ...style,
      }}
    >
      {options?.onResize ? (
        <RefResizeObserver onResize={options?.onResize}>
          <ResizeWrapper
            style={{
              gridTemplateColumns: columnGrid
                ? columns?.map(({ width }) => width).join(" ")
                : undefined,
              minHeight: virtual ? virtualRowHeight : undefined,
            }}
          >
            {element(isSorting ?? false)}
          </ResizeWrapper>
        </RefResizeObserver>
      ) : (
        element(isSorting ?? false)
      )}
    </TableRow>
  );

  const updateVisibleRows = () => {
    let firstVisible = -1;
    let lastVisible = -1;
    const target = scrollRef.current;
    if (target && onScroll) {
      const children = target.children;
      Array.from(children).forEach((child, index) => {
        const element = child as HTMLDivElement;
        const elemTop = element.offsetTop;
        const elemBottom = element.offsetTop + element.offsetHeight;
        const visible =
          elemTop < target.scrollTop + target.offsetHeight &&
          elemBottom >= target.scrollTop;
        if (visible) {
          if (firstVisible === -1) {
            firstVisible = index;
          }
          lastVisible = index;
        }
      });
      setVisibleRows(({ first, last }) => {
        if (firstVisible !== first || lastVisible !== last) {
          onScroll?.(firstVisible, lastVisible);
          return { first: firstVisible, last: lastVisible };
        }
        return { first, last };
      });
    }
  };

  useEffect(() => {
    updateVisibleRows();
  }, [rows?.length]);

  const tableRows = rows?.map((row, index) =>
    isSorting ? (
      <Draggable draggableId={row.id} index={index} key={row.id}>
        {(provided, snapshot) => makeRow(row, index, { provided, snapshot })}
      </Draggable>
    ) : (
      makeRow(row, index, {
        onResize: onScroll ? updateVisibleRows : undefined,
      })
    )
  );

  let headerHeight = 0;
  if (columns?.some(({ title }) => title)) {
    headerHeight += 30;
  }
  if (isSorting) {
    headerHeight += 30;
  }
  const minRowCount = Math.max(1, Math.min(2, rows?.length ?? 0));

  return (
    <DragDropContext
      onDragEnd={(result) => {
        if (result.destination) {
          onSort?.(result.source.index, result.destination.index);
        }
      }}
    >
      <RefResizeObserver
        onResize={(size) => {
          const width = Math.round(size.offsetWidth);
          setCurrentWidth((prev) => {
            if (width && width !== prev) {
              updateOverflow();
              gridRef.current?.recomputeGridSize();
              return width;
            }
            return prev;
          });
        }}
      >
        <StyledTable
          $autoHeight={autoHeight}
          style={{
            height,
            maxHeight,
            minHeight: currentRowHeight * minRowCount + headerHeight,
          }}
          className={className}
        >
          {isSorting && (
            <header>
              <Reorder>
                <Move size={15} />
                <span>
                  {sortingTitle ?? "Drag and drop rows to change their order."}
                </span>
              </Reorder>
            </header>
          )}
          {columns?.some(({ title }) => title) && (
            <header
              style={{
                gridTemplateColumns: columns?.reduce(
                  (str, { width }) => str + " " + width,
                  ""
                ),
                padding: rowPadding,
                paddingRight:
                  rowPaddingRight + (isOverflow || isVirtual ? scrollWidth : 0),
              }}
            >
              {columns?.map((column, index) => {
                const { title, onSort } = column;
                const isActive = sortState?.columnIndex === index;
                const direction = isActive ? sortState!.direction : null;
                if (onSort) {
                  return (
                    <div key={index}>
                      <SortableHeader
                        $active={isActive}
                        onClick={() => handleColumnSort(index, column)}
                      >
                        <span>{title}</span>
                        {isActive ? (
                          direction === "asc" ? (
                            <ArrowUp size={17} />
                          ) : (
                            <ArrowDown size={17} />
                          )
                        ) : (
                          <></>
                        )}
                      </SortableHeader>
                    </div>
                  );
                }
                return <span key={index}>{title}</span>;
              })}
            </header>
          )}
          {isVirtual ? (
            rows && (
              <AutoSizeParent>
                <AutoSizer>
                  {({ height, width }) => (
                    <Grid
                      estimatedRowSize={virtualRowHeight}
                      rowCount={rows.length}
                      columnCount={1}
                      columnWidth={width - scrollWidth}
                      width={width}
                      height={height}
                      rowHeight={cache.rowHeight}
                      deferredMeasurementCache={cache}
                      cellRenderer={({ rowIndex, key, parent, style }) => (
                        <CellMeasurer
                          cache={cache}
                          columnIndex={0}
                          key={key}
                          parent={parent}
                          rowIndex={rowIndex}
                        >
                          {({ registerChild, measure }) =>
                            makeRow(rows[rowIndex], rowIndex, {
                              style,
                              ref: registerChild,
                              onResize: ({ height }) => {
                                const cachedHeight = cache.getHeight(
                                  rowIndex,
                                  0
                                );
                                if (
                                  height &&
                                  height + 2 !== cachedHeight &&
                                  height + 3 !== cachedHeight
                                ) {
                                  measure();
                                  updateOverflow();
                                }
                              },
                            })
                          }
                        </CellMeasurer>
                      )}
                      ref={gridRef}
                    />
                  )}
                </AutoSizer>
              </AutoSizeParent>
            )
          ) : (
            <Scroll
              ref={scrollRef}
              onScroll={() => {
                updateVisibleRows();
              }}
            >
              {isLoadingTop && <Spinner />}
              {isSorting ? (
                <Droppable droppableId="table">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {tableRows}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              ) : (
                tableRows
              )}
              {isLoading ? (
                <Spinner />
              ) : (
                !rows?.length &&
                !isLoadingTop && <Banner placeholder="No entries" />
              )}
            </Scroll>
          )}
        </StyledTable>
      </RefResizeObserver>
    </DragDropContext>
  );
});

export default Table;
